import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Map, APIProvider, useMap } from "@vis.gl/react-google-maps";
import { MapIcon } from "lucide-react";
import DrawingExample from "./drawing-example";
import { useDepots } from "@/api/depots";
import {
  CreateServiceAreaFormData,
  createServiceAreaSchema,
  useCreateServiceArea,
} from "@/api/service-areas";

interface CenterPolygonButtonProps {
  boundaryPolygon?: { type: string; coordinates: number[][][] };
  className?: string;
}

export function CenterPolygonButton({
  boundaryPolygon,
  className = "absolute bottom-4 left-4",
}: CenterPolygonButtonProps) {
  const map = useMap();

  const centerOnPolygon = () => {
    if (
      boundaryPolygon &&
      boundaryPolygon?.coordinates?.[0]?.length > 0 &&
      map
    ) {
      const bounds = new google.maps.LatLngBounds();
      boundaryPolygon.coordinates[0].forEach((coord) => {
        bounds.extend({ lat: coord[1], lng: coord[0] });
      });
      map.fitBounds(bounds);
    }
  };

  if (!boundaryPolygon?.coordinates?.length) {
    return null;
  }

  return (
    <Button
      variant="default"
      onClick={centerOnPolygon}
      className={className}
      size="sm"
    >
      <MapIcon className="h-4 w-4 mr-2" />
      Center Polygon
    </Button>
  );
}

// Create a separate component for the map to use useMap hook
function ServiceAreaMap({
  onPolygonComplete,
  form,
  mode,
}: {
  onPolygonComplete: (polygon: {
    type: string;
    coordinates: number[][][];
  }) => void;
  form: UseFormReturn<CreateServiceAreaFormData>;
  mode: "create" | "edit";
}) {
  return (
    <>
      <DrawingExample
        onPolygonComplete={onPolygonComplete}
        initialPolygon={mode === "edit" ? form.watch("boundary") : undefined}
      />
      <CenterPolygonButton boundaryPolygon={form.watch("boundary")} />
    </>
  );
}

interface CreateServiceAreaFormProps {
  mode?: "create" | "edit";
  defaultValues?: Partial<CreateServiceAreaFormData>;
  onSubmit?: (values: CreateServiceAreaFormData) => void;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export function CreateServiceAreaForm({
  mode = "create",
  defaultValues,
  onSubmit,
  onCancel,
  onSuccess,
}: CreateServiceAreaFormProps) {
  const defaultCenter = { lat: 43.6532, lng: -79.3832 };

  const { data: depots, isLoading } = useDepots();

  const form = useForm<CreateServiceAreaFormData>({
    resolver: zodResolver(createServiceAreaSchema),
    defaultValues: defaultValues ?? {
      code: "",
      name: "",
      isActive: true,
      boundary: {
        type: "Polygon",
        coordinates: [[[0, 0]]],
      },
      postcodes: [],
      metadata: {},
      depotId: "",
      operatingWindows: [
        {
          type: "PICKUP",
          daysOfWeek: [1, 2, 3, 4, 5],
          startTime: "09:00",
          endTime: "17:00",
          capacity: 100,
          isActive: true,
        },
      ],
    },
  });

  const createServiceAreaMutation = useCreateServiceArea({
    onSuccess: () => {
      form.reset();
      onSuccess?.();
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    if (onSubmit) {
      onSubmit(values);
    } else {
      createServiceAreaMutation.mutate(values);
    }
  });

  const addOperatingWindow = () => {
    const currentWindows = form.getValues("operatingWindows");
    form.setValue("operatingWindows", [
      ...currentWindows,
      {
        type: "PICKUP",
        daysOfWeek: [1, 2, 3, 4, 5],
        startTime: "09:00",
        endTime: "17:00",
        capacity: 100,
        isActive: true,
      },
    ]);
  };

  return (
    <div className="flex flex-col overflow-y-auto max-h-[80vh]">
      <div className="h-[300px] flex-shrink-0 mb-4 relative">
        <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
          <Map
            defaultCenter={defaultCenter}
            defaultZoom={13}
            gestureHandling={"greedy"}
            disableDefaultUI={true}
            style={{ width: "100%", height: "100%" }}
          >
            <ServiceAreaMap
              onPolygonComplete={(polygon) => {
                form.setValue("boundary", polygon);
              }}
              form={form}
              mode={mode}
            />
          </Map>
        </APIProvider>
      </div>

      <div className="flex-1">
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-6 px-4">
            {/* Basic Information Section */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormControl>
                      <Input placeholder="SA-001" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Downtown Service Area" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="depotId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Depot</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a depot" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isLoading ? (
                          <SelectItem value="loading">
                            Loading depots...
                          </SelectItem>
                        ) : depots && depots.length > 0 ? (
                          depots.map((depot) => (
                            <SelectItem key={depot.id} value={depot.id}>
                              {depot.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-depots">
                            No depots available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-4 rounded-lg border p-4">
                    <div className="flex-1">
                      <FormLabel>Active Status</FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Postcodes Section */}
            <FormField
              control={form.control}
              name="postcodes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postcodes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter postcodes (one per line)"
                      className="h-24"
                      {...field}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value
                            .split("\n")
                            .filter((code) => code.trim())
                        )
                      }
                      value={field.value.join("\n")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Boundary GeoJSON */}
            <FormField
              control={form.control}
              name="boundary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Boundary (GeoJSON)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='{"type": "Polygon", "coordinates": [[[lng, lat], ...]]}'
                      className="h-32 font-mono text-sm"
                      {...field}
                      value={JSON.stringify(field.value, null, 2)}
                      onChange={(e) => {
                        try {
                          field.onChange(JSON.parse(e.target.value));
                        } catch (error) {
                          // Handle invalid JSON
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Operating Windows Section */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Operating Windows</h3>
                <Button
                  type="button"
                  variant="outline"
                  onClick={addOperatingWindow}
                >
                  Add Window
                </Button>
              </div>

              {form.watch("operatingWindows").map((_, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">Window #{index + 1}</h4>
                    {index > 0 && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          const current = form.getValues("operatingWindows");
                          form.setValue(
                            "operatingWindows",
                            current.filter((_, i) => i !== index)
                          );
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name={`operatingWindows.${index}.type`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Type</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="PICKUP">Pickup</SelectItem>
                              <SelectItem value="DELIVERY">Delivery</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`operatingWindows.${index}.capacity`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Capacity</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) =>
                                field.onChange(parseInt(e.target.value))
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name={`operatingWindows.${index}.startTime`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Time</FormLabel>
                          <FormControl>
                            <Input type="time" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`operatingWindows.${index}.endTime`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Time</FormLabel>
                          <FormControl>
                            <Input type="time" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name={`operatingWindows.${index}.daysOfWeek`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Days of Week</FormLabel>
                        <div className="flex gap-2">
                          {[
                            "Mon",
                            "Tue",
                            "Wed",
                            "Thu",
                            "Fri",
                            "Sat",
                            "Sun",
                          ].map((day, i) => (
                            <div
                              key={day}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                checked={field.value.includes(i + 1)}
                                onCheckedChange={(checked) => {
                                  const current = new Set(field.value);
                                  if (checked) {
                                    current.add(i + 1);
                                  } else {
                                    current.delete(i + 1);
                                  }
                                  field.onChange([...current]);
                                }}
                              />
                              <span className="text-sm">{day}</span>
                            </div>
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-4">
              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button type="submit">
                {mode === "create"
                  ? "Create Service Area"
                  : "Update Service Area"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
